import { NumberOrNull, StringOrNull } from '../entity/types';
import { itemStatus } from './providerSlice';
import moment from 'moment-jalaali';
import IconTypes from '../../components/Icon/IconTypes';
import { ILocal } from '../../components/Chart/BaseDatePicker';

export type TagType = 'General' | 'Identification';

export type PROVIDER_CONFIG_FLAG =
  | 'limited'
  | 'haveDashboard'
  | 'hidden'
  | 'hideTitleOnHomePage';

export type loadingStatus = 'loading' | 'notLoaded' | 'isLoading' | 'loaded';

export type QuestionProtection = 'Edit' | 'View';

export const ITEM_MIN_ORDER = -1e20;

export type ProviderItemType =
  | 'chart'
  | 'document'
  | 'file'
  | 'folder'
  | 'item'
  | 'link'
  | 'quill'
  | 'searchTag'
  | 'survey'
  | 'surveyReport'
  | 'externalLink';

export type ReportViewType =
  | 'Dashboard'
  | 'List'
  | 'BSC'
  | 'MiniDashboard'
  | 'Fullscreen';
export type EndTime =
  | 'Yesterday'
  | 'Today'
  | 'EndOfWeek'
  | 'EndOfMonth'
  | 'EndOfSeason'
  | 'EndOfHalfYear'
  | 'EndOfYear'
  | 'LastMonth'
  | 'LastSeason'
  | 'LastHalfYear'
  | 'LastYear';

export const DataType = {
  date: 'date',
  dateTime: 'dateTime',
  number: 'number',
  user: 'user',
  tag: 'tag',
  Null: 'Null',
  question: 'question',
  answer: 'answer',
};

export interface IRelatedProviderItem {
  id: number;
  providerId: number;
  title: string;
  type: ProviderItemType;
}

export interface ITaskReportInfo {
  id?: number;
  timeLength: number;
  projectId?: number;
  companyId: number;
  parentId?: number;
  templateId: number;
  assigneeId?: number;
  splitMonths: number;
  splitField: string;
  fieldMap: { [key: string]: string };
  order: number;
  dashboardWidth?: number;
  dashboardHeight?: number;
  showRelative: boolean;
  name?: string;
  visiblePart: string;
  type: 'task';
  tags: string[];
}

export const providerItemType = {
  document: 'document',
  folder: 'folder',
  link: 'link',
  item: 'item',
  survey: 'survey',
  chart: 'chart',
  searchTag: 'searchTag',
  surveyReport: 'surveyReport',
  report: 'report',
  itemLink: 'itemLink',
  externalLink: 'externalLink',
};

export const FilterType = {
  term: 'termFilter',
  range: 'rangeFilter',
  dateTerm: 'dateTermFilter',
  dateRange: 'dateRangeFilter',
  scoreRange: 'rangeFilter',
};

export interface IDocumentView {
  id?: number;
  url: string;
  deleted?: boolean;
  fileSize: number;
  uploading?: boolean;
  properties: IDocumentProperties;
  uploadDate?: number;
  documentType?: string;
  thumbnailUrl?: string;
  expirationDate?: number;
}

export interface ITagView {
  id: number | string;
  text: string;
  deleted?: boolean;
  order?: number;
  parentId?: NumberOrNull;
  type?: TagType;
  universal?: boolean;
  status?: itemStatus;
}

export interface ITags {
  // [key: number]: ITagView;
  data: { [key: number]: ITagView };
  status: loadingStatus | itemStatus;
}

export const getDateOf = (e: EndTime) => {
  let month = moment().jMonth();
  switch (e) {
    case 'EndOfWeek':
      return moment().endOf('week');
    case 'EndOfMonth':
      return moment().endOf('jMonth');
    case 'EndOfSeason':
      month = (Math.floor(month / 3) + 1) * 3 - 1;
      break;
    case 'EndOfHalfYear':
      month = (Math.floor(month / 6) + 1) * 6 - 1;
      break;
    case 'EndOfYear':
      month = 11;
      break;
    case 'LastMonth':
      return moment().add(-1, 'jMonth').endOf('jMonth');
    case 'LastSeason':
      month = (Math.floor(month / 3) + 1) * 3 - 1 - 3;
      break;
    case 'LastHalfYear':
      month = (Math.floor(month / 6) + 1) * 6 - 1 - 6;
      break;
    case 'LastYear':
      month = 11 - 12;
      break;
    case 'Today':
      return moment().endOf('day');
    case 'Yesterday':
      return moment().endOf('day').add(-1, 'day');
  }
  return moment()
    .add(month - moment().jMonth(), 'jMonth')
    .endOf('jMonth');
};

export interface ActivePermissions {
  effectiveReadPermission?: string[];
  effectiveReportPermission?: string[];
  effectiveChangeDataPermission?: string[];
  effectiveEditPermission?: string[];
  effectivePathViewPermission?: string[];
}

export interface IProviderItemViewWithData {
  data: IProviderItemView;
  status?: itemStatus;
  isLoading?: boolean;
  state?: string;
  error?: unknown;
  progress?: number;
}

export interface IProviderItems {
  items: { [key: number]: IProviderItemViewWithData };
  folders: { [key: number]: { state: string } };
  search: { [key: string]: number[] };
  flag?: { providerSurveyStatus?: loadingStatus };
}

export type Field = { name: string; value: string };

export type ScoreMethodType = 'sum' | 'totalAverage';
export type VisibilityStrategy = 'personal' | 'protected' | 'open';
export type NotificationMedium = 'SMS' | 'Slack';

export interface INotifGroup {
  groupId: string;
  groupTitle: string;
  userIds: number[];
}

export interface IDocumentProperties {
  photo?: boolean;
  order?: number;
  format: string;
  displayName: string;
}

export interface IQuestionField {
  name: string;
  options: string[] | null;
  value: string;
  valueList: string[] | null;
}

export interface IViewConditionView {
  refQuestionId: number;
  questionValues: string[];
}

export interface IProviderItemView {
  allowComment: boolean;
  allowLike: boolean;
  templateType?: string;
  hidden?: boolean;
  changeDataLimitationId: NumberOrNull;
  dadId: number;
  description: StringOrNull;
  document?: any; // IDocumentView;
  editable: boolean;
  editLimitationId: NumberOrNull;
  fastAccess: string[];
  linkedProviderId?: number;
  username?: string;
  id: number;
  implicitFastAccess: string[];
  itemLinks: IProviderItemLinkView[];
  providerId: number;
  name?: StringOrNull;
  order: number;
  preserveParentEditLimitation: boolean;
  activePermissions?: ActivePermissions;
  parentActivePermissions?: ActivePermissions;
  readLimitationId: NumberOrNull;
  referenced: IProviderItemLinkView[];
  reportLimitationId: NumberOrNull;
  showViewCount: boolean;
  status: string;
  tags: ITagView[];
  thumbnailUrl: string;
  type: ProviderItemType;
  url?: string;
  creationDate: number;
  identifierValues: { [key: number]: string };
  surveyId?: string | number;
  progress?: number;
  virtualChildren?: any;
  myResponseInfo?: {
    filledPeriods: Array<string>;
  };
  reportToken?: string;
  editToken?: string;
  changeDataToken?: string;
  formatedName?: string;
  mandatoryTagIds?: Array<number>;
  viewType?: string;
  title?: string;
  moreInfoUrl?: string;
  upstreamItemId?: number;
}
export interface SurveyOwnerProperties {
  addedTagIds: number[];
  allowGlobalReport?: boolean;
  localId?: ILocal | null;
  timeZoneId?: string | null;
}

export interface IBotProperties {
  assistantId?: string;
  defaultPrompts: string[];
  enableBotIntegration: boolean;
  systemInstructions?: string;
  chatButtonTitle?: string;
  chatHistoryItemId?: string;
  chatHistoryItemViewToken?: string;
  showCitation: boolean;
  vectorStoreId?: string;
  reasoningEffort?: 'minimal' | 'low' | 'medium' | 'high';
  temperature?: number;
  topP?: number;
  verbosity?: 'low' | 'medium' | 'high';
  model?: string;
}
export interface IProvider {
  address: any;
  bookmark: any;
  categories: number[];
  description: StringOrNull;
  documents: IProviderItemView[];
  id: number;
  username: string;
  infoSurveyResponse: any;
  managingCompanyId: NumberOrNull;
  limitAccessToCompany: boolean;
  useVirtualSpaces: boolean;
  allowSeeThroughItems: boolean;
  name: string;
  publicProperties: any;
  rootDocument: IProviderItemView;
  status: { register: string; active: string };
  survey: any;
  template: any;
  tags: any;
  user: number;
  surveyOwnerProperties: SurveyOwnerProperties;
  surveyOwnerToken?: string;
  configFlag: { [config in PROVIDER_CONFIG_FLAG]: string };
  botProperties: IBotProperties;
}

export interface IProviders {
  items: { [key: number]: IProviderItems };
  tags: { [key: number]: ITags | null };
  usernameMap: { [key: string]: number };
  map: {
    [key: string | number]: {
      data: IProvider | null;
      isLoading: boolean;
      isValid?: boolean;
    };
  };
  myProvidersStatus?: loadingStatus;
}

export interface IProviderItemLinkView {
  id: number;
  sourceItemId: number;
  destinationItemId: number;
  order: number;
  description: string;
}

export interface VersionProperties {
  name: string;
  message: string;
}
export interface IDocumentVersion {
  creationDate: number;
  creatorId: number;
  document: IDocumentView;
  properties: VersionProperties;
}

export interface IProviderDocumentViewObj extends IProviderItemView {
  currentDocumentId: number;
  versions: IDocumentVersion[];
}

export type ItemSearchField =
  | 'name'
  | 'exactName'
  | 'tag'
  | 'parentTag'
  | 'exactTag'
  | 'exactParentTag';

export const getProviderIcon: { [key in ProviderItemType]: IconTypes } = {
  item: 'file',
  file: 'file',
  link: 'user',
  chart: 'alert-triangle',
  quill: 'align-center',
  folder: 'folder',
  survey: 'check-circle',
  document: 'file-text',
  searchTag: 'tag',
  surveyReport: 'pie-chart',
  externalLink: 'link',
};

export interface IProviderLinkView extends IProviderItemView {
  providerName: string;
  type: 'link';
  botIntegrationProperties: IBotProperties;
}
